import { useMemo } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import type { ProcurementNeedMarket } from '@ebalance/proto/bffuser/v1/supply_demand_gap_model_pb'

import { useGetProcurementNeed } from '~/hooks/bidding/useGetProcurementNeed'
import {
  combineNeedByKoma,
  needOfChartAreas,
  perAreaNeedByKoma,
} from '~/models/procurementNeed'

export type ProcurementNeedChartData = {
  /**
   * チェック中エリアをコマ別合算した need[] (= GetProcurementNeed 由来)。取得未完了/失敗と、
   * 区分を指定してチェック中エリアの series が返らなかった時 (区分がそのエリアに計画を持たない)
   * は undefined。 区分なしの need は要求した全エリアの series を返す。
   */
  needByKoma: number[] | undefined
  /**
   * エリア別・コマ別の need(MW) Map (= 合算しない。一括作成 AC-005a 案B 用)。
   * 取得未完了/失敗は undefined。エリアフィルタに依存せず取得済み全エリアを保持する。
   */
  needByArea: Map<AreaCode, number[]> | undefined
  /** need RPC が取得エラーのとき true (= need line 非表示 + 注記の trigger)。 */
  needUnavailable: boolean
}

/**
 * ポジションチャートの need 配線 (RPC 取得 → per-area 合算 → エラー判定) を
 * SpotBidding / IntradayBidding page 間で共通化する。両 page とも
 * `useGetProcurementNeed` → `combineNeedByKoma` → `isError` の同一手順を重複実装して
 * いたため、ここに集約する (code-quality)。
 *
 * @param date - 対象受渡日 (spot は表示中の targetDate、 intraday は当日)
 * @param selectedAreas - チャートに載せるエリア (= needByKoma の合算対象)
 * @param market - SPOT / INTRADAY (= 呼び出し元の市場に応じて渡す)
 * @param requestAreaCodes - RPC に投げるエリア (省略時は selectedAreas)。 チャート対象より
 *   広いエリアの need が要る画面 (= spot の一括作成は AC-BSP-005a で複数エリア選択、
 *   一方チャートは AC-BSP-016a で単一エリア) はここに全エリアを渡し、 needByArea を
 *   一括作成へ、 needByKoma をチャートへ供給する。
 */
export function useProcurementNeedChartData(
  date: Date,
  selectedAreas: Set<AreaCode>,
  market: ProcurementNeedMarket,
  requestAreaCodes?: readonly AreaCode[],
  options?: {
    /**
     * false の間は RPC を発火しない (issue #7131 / AC-WFW-006a Decision 1:
     * ワークフローケース対象日が確定するまで対象日依存のデータ取得を開始しない)。
     * 省略時は従来どおり常時有効 (= 後方互換)。
     */
    enabled?: boolean
    /** 発注主体区分 ([[useGetProcurementNeed]] の同名 option)。 省略時はエリア全体。 */
    issuerType?: LicenseType
  },
): ProcurementNeedChartData {
  const areaList = useMemo(
    () =>
      requestAreaCodes !== undefined
        ? [...requestAreaCodes]
        : [...selectedAreas],
    [requestAreaCodes, selectedAreas],
  )
  const needQuery = useGetProcurementNeed(date, areaList, market, {
    enabled: options?.enabled,
    issuerType: options?.issuerType,
  })
  const needByKoma = useMemo(
    () =>
      needQuery.data !== undefined
        ? options?.issuerType === undefined
          ? combineNeedByKoma(needQuery.data.areaSeries, selectedAreas)
          : needOfChartAreas(needQuery.data.areaSeries, selectedAreas)
        : undefined,
    [needQuery.data, selectedAreas, options?.issuerType],
  )
  const needByArea = useMemo(
    () =>
      needQuery.data !== undefined
        ? perAreaNeedByKoma(needQuery.data.areaSeries)
        : undefined,
    [needQuery.data],
  )
  return { needByKoma, needByArea, needUnavailable: needQuery.isError }
}
